/**
 * The change-password screen (epic 076 task 03) — mounted OUTSIDE the Layout
 * route. Reachable from the sidebar's user-block menu (HF-144) and the forced
 * way (the auth provider redirects every other path here while
 * `must_change_password` is set).
 * Current + new + confirm; on success the provider's `me` is refreshed (which
 * clears the forced redirect) and the browser navigates back to the return
 * path the login carried, else `/`.
 * A 401 maps by the api's body message (QA round 18 #139 ride-along):
 * 'invalid credentials' is a wrong current password; anything else is a
 * session failure — stated honestly with a re-login offer, never as a
 * wrong-password claim.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { INVALID_CREDENTIALS_MESSAGE, changePassword, useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/** Same in-app-only guard as the login screen's return handling. */
function safeReturnPath(raw: string | null): string | null {
  return raw !== null && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
}

export function ChangePassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    setPending(true);
    setError(null);
    setSessionExpired(false);
    try {
      await changePassword(currentPassword, newPassword);
      await refresh();
      const returnTo = safeReturnPath(searchParams.get('return'));
      navigate(returnTo ?? '/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (err.message === INVALID_CREDENTIALS_MESSAGE) {
          // The api's wrong-current-password 401 carries exactly this body
          // message (auth.service.ts INVALID_CREDENTIALS) — the only honest
          // basis for the wrong-password claim.
          setError('Current password is incorrect.');
        } else {
          // Any other 401 is the guard rejecting the session itself
          // (missing, expired or revoked cookie — Nest's bare 'Unauthorized'
          // body): the current password was never checked, so no
          // wrong-password claim. The provider state syncs via refresh()
          // (its `me` will 401 and clear the user); the re-login offer below
          // is the way back in.
          setError('Your session has expired. Your password was not changed.');
          setSessionExpired(true);
          void refresh();
        }
      } else {
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : 'Password change failed. Try again.'
        );
      }
    } finally {
      setPending(false);
    }
  };

  /** The re-login offer after a session-expired 401: back to the sign-in
   *  screen, carrying the same return path this screen received, so the
   *  forced-change chain (login → change-password → return) survives. */
  const handleSignInAgain = () => {
    const returnTo = safeReturnPath(searchParams.get('return'));
    navigate(returnTo === null ? '/login' : `/login?return=${encodeURIComponent(returnTo)}`, {
      replace: true,
    });
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent font-display text-[16px] font-bold text-white">
            T
          </div>
          <div className="leading-tight">
            <div className="font-display text-[16px] font-semibold text-ink">TradingTips</div>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-muted">
              Lead Ops Console
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Change password</CardTitle>
            <CardDescription>
              {user === null
                ? 'Your account requires a new password before you can continue.'
                : `Signed in as ${user.email}. Choose a new password to continue.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                {error !== null && (
                  <p role="alert" className="text-[13px] text-destructive">
                    {error}
                  </p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="w-full pt-1">
                  <Button type="submit" variant="primary" className="w-full" disabled={pending}>
                    {pending ? 'Saving...' : 'Save new password'}
                  </Button>
                </div>
                {sessionExpired && (
                  <div className="w-full">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleSignInAgain}
                    >
                      Sign in again
                    </Button>
                  </div>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
